import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef } from "react";
import { useAuth, DEPARTMENT_LABELS } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { AppHeader } from "@/components/app/AppHeader";
import { MfaCard } from "@/components/settings/MfaCard";
import { ChangePasswordCard } from "@/components/settings/ChangePasswordCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/settings")({ component: SettingsPage });

function SettingsPage() {
  const { profile, user, refresh } = useAuth();
  const [fullName, setFullName] = useState(profile?.full_name ?? "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const saveProfile = async () => {
    if (!user) return;
    setBusy(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName })
      .eq("id", user.id);
    setBusy(false);
    if (error) toast.error(error.message);
    else {
      toast.success("Profile updated");
      refresh();
    }
  };



  const onAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    const ext = file.name.split(".").pop();
    const path = `${user.id}/avatar.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) return toast.error(upErr.message);
    const { data } = supabase.storage.from("avatars").getPublicUrl(path);
    const url = `${data.publicUrl}?t=${Date.now()}`;
    const { error } = await supabase
      .from("profiles")
      .update({ avatar_url: url })
      .eq("id", user.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Avatar updated");
      refresh();
    }
  };

  return (
    <>
      <AppHeader subtitle="Manage your profile and password" />
      <div className="grid gap-4 px-[22px] py-4 lg:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-5 shadow-sm">
          <h2 className="text-[16px] font-bold">Profile</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Update your display name and avatar.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold text-white"
              style={{ background: "var(--gradient-avatar)" }}
            >
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="h-full w-full rounded-full object-cover" />
              ) : (
                (profile?.full_name || profile?.email || "U").charAt(0).toUpperCase()
              )}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onAvatar} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                Change avatar
              </Button>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Full name</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input value={profile?.email ?? ""} disabled />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Department</Label>
                <Input value={profile ? DEPARTMENT_LABELS[profile.department] : ""} disabled />
              </div>
              {profile?.department === "editor" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Monthly target</Label>
                  <Input value={String(profile?.monthly_target ?? "")} disabled />
                </div>
              )}
            </div>
            <Button onClick={saveProfile} disabled={busy}>
              Save profile
            </Button>
          </div>
        </div>

        <ChangePasswordCard />

        <MfaCard />
      </div>
    </>
  );
}
